/*
 * Copyright (c) 2020 Staffino, s.r.o. - All Rights Reserved
 * Unauthorized copying of this file, via any medium is strictly prohibited
 * Proprietary and confidential
 * Created by Martin Komara, April 2020
 */

import EventEmitter from 'events';
import filter from 'lodash/fp/filter';
import queryString from 'query-string';
import { FlowObject, IFlowBridge } from '@staffino/flow-screen';
import { FlowAdditionalProps, FlowState } from '@staffino/model';

type ConstructorParameters = {
  sessionId?: string;
  flowId: string;
  targetId: string;
  customerId?: string;
  interactionId?: string;
  source?: string;
  medium?: string;
  reason?: string;
  objects: FlowObject[];
  parameters?: Record<string, string | number | boolean | Date | null>;
  localeId?: string;
  status?: 'committed' | 'limit-reached' | 'expired' | 'removed' | 'ready';
  flowState?: FlowState;
  versionId?: string;
};
export default class CachedFlowBridge
  extends EventEmitter
  implements IFlowBridge
{
  readonly assets = {};

  readonly sessionId?: string;

  readonly flowId: string;

  readonly targetId: string;

  readonly customerId?: string;

  readonly interactionId?: string;

  readonly source?: string;

  readonly medium?: string;

  readonly reason?: string;

  readonly parameters?: Record<string, string | number | boolean | Date | null>;

  localeId: string;

  objects: FlowObject[];

  versionId?: string;

  readonly flowState?: FlowState;

  status?: 'committed' | 'limit-reached' | 'expired' | 'removed' | 'ready';

  constructor(params: ConstructorParameters) {
    super();
    const {
      objects = [],
      sessionId,
      flowId,
      targetId,
      customerId,
      interactionId,
      source,
      medium,
      reason,
      parameters,
      localeId,
      status,
      versionId,
      flowState,
    } = params;
    this.objects = objects;
    this.sessionId = sessionId;
    this.flowId = flowId;
    this.targetId = targetId;
    this.customerId = customerId;
    this.interactionId = interactionId;
    this.parameters = parameters;
    this.source = source;
    this.medium = medium;
    this.reason = reason;
    this.localeId = localeId;
    this.status = status;
    this.versionId = versionId;
    this.flowState = flowState;
  }

  async load(condition: Partial<FlowObject>): Promise<FlowObject[]> {
    return filter(condition, this.objects);
  }

  async commitState(
    state: FlowState,
    additionalProps?: FlowAdditionalProps,
  ): Promise<void> {
    const response = await fetch('/api/flow-states/commits', {
      method: 'POST',
      mode: 'cors',
      cache: 'no-cache',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
      },
      redirect: 'error',
      referrerPolicy: 'no-referrer',
      body: JSON.stringify({
        state: { ...state, localeId: this.localeId },
        sessionId: this.sessionId,
        flowId: this.flowId,
        targetId: this.targetId,
        customerId: this.customerId,
        interactionId: this.interactionId,
        source: this.source,
        medium: this.medium,
        reason: this.reason,
        leftAt: new Date(),
        parameters: this.parameters,
        autoCommitted: additionalProps?.autoCommitted,
      }),
    });
    if (!response.ok) {
      throw new Error(response.statusText || response.status.toString());
    }
    console.log('API response: ', response);
  }

  async updateFlowSession(state: FlowState): Promise<void> {
    const response = await fetch(`/api/flow-sessions/${this.sessionId}`, {
      method: 'PUT',
      mode: 'cors',
      cache: 'no-cache',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
      },
      redirect: 'error',
      referrerPolicy: 'no-referrer',
      body: JSON.stringify({
        state: { ...state, localeId: this.localeId },
        sessionId: this.sessionId,
        flowId: this.flowId,
        targetId: this.targetId,
        customerId: this.customerId,
        interactionId: this.interactionId,
        source: this.source,
        medium: this.medium,
        reason: this.reason,
        leftAt: new Date(),
      }),
    });
    console.log('API response: ', response);
  }

  reportError(error: Error) {
    console.error(error.message);
    this.emit('error', error);
  }

  async setLocale(id: string, code: string): Promise<void> {
    const url = `/api/flow/${this.flowId}/objects?localeId=${id}&targetId=${this.targetId}&versionId=${this.versionId}`;
    const response = await fetch(url, {
      method: 'GET',
      mode: 'cors',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
      },
      redirect: 'error',
      referrerPolicy: 'no-referrer',
    });
    if (!response.ok) {
      throw new Error(response.statusText || response.status.toString());
    }
    const blockObjects = await response.json();

    // this merging should be made smarter
    const newFlowObject = blockObjects.find(
      (block) => block.objectType === 'flow',
    );
    const oldFlowObject = this.objects.find(
      (block) => block.objectType === 'flow',
    );
    this.objects = [
      { ...oldFlowObject, ...newFlowObject },
      ...blockObjects.filter((block) => block.objectType !== 'flow'),
      ...filter({ objectType: 'target-lineage' }, this.objects),
      ...filter({ objectType: 'customer' }, this.objects),
      ...filter({ objectType: 'interaction' }, this.objects),
      ...filter({ objectType: 'throttling' }, this.objects),
    ];
    this.localeId = id;

    // update locale in url
    const queryParams = queryString.parse(location.search);
    const updatedQuery = queryString.stringify({
      ...queryParams,
      locale: code,
    });
    history.pushState(null, null, `?${updatedQuery}`);

    this.emit('update');
  }
}
